import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaFilter, FaCheckCircle, FaClock, FaMoneyCheckAlt, FaTimesCircle, FaPause, FaHistory, FaHandHoldingUsd, FaUniversity, FaCloudUploadAlt, FaFileContract, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import './Payouts.css';
import Pagination from '../Common/Pagination';
import { SINGAPORE_BANKS } from '../../utils/constants';

const PayoutsList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [reason, setReason] = useState('');
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [stats, setStats] = useState({
    totalAmount: 0,
    pendingAmount: 0,
    completedCount: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  const [modalAction, setModalAction] = useState(''); // 'reject' or 'hold'
  
  // Detail Modal & Proof State
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchPayouts = useCallback(async () => {
    if (isFirstLoad) setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter === 'all' ? undefined : statusFilter.toUpperCase(),
        search: searchTerm || undefined
      };
      
      const [payoutsRes, statsRes] = await Promise.all([
        api.get(ENDPOINTS.PAYOUTS.base, { params }),
        api.get(ENDPOINTS.PAYOUTS.stats)
      ]);

      const payoutsData = payoutsRes.data || payoutsRes;
      const statsData = statsRes.data || statsRes;

      setPayouts(payoutsData.data || []);
      setPagination(prev => ({ 
        ...prev, 
        total: payoutsData.meta?.total || 0 
      }));
      
      // Map stats dynamically from backend response
      setStats({
        totalAmount: statsData.totalAmount || 0,
        pendingAmount: statsData.pendingAmount || 0,
        completedCount: statsData.completedCount || 0
      });
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
      toast.error('Failed to load payout data');
    } finally {
      setLoading(false);
      setIsFirstLoad(false);
    }
  }, [pagination.page, pagination.limit, statusFilter, searchTerm, isFirstLoad]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchPayouts();
    }, 300);
    return () => clearTimeout(handler);
  }, [fetchPayouts]);

  const handleProcess = async (id) => {
    if (!window.confirm('Mark this payout as currently processing? This signals that you have started the bank transfer.')) return;
    
    setActionLoading(id);
    try {
      await api.post(`${ENDPOINTS.PAYOUTS.base}/${id}/process`);
      toast.info('Payout marked as processing');
      fetchPayouts();
    } catch (error) { 
      toast.error(error.response?.data?.message || 'Failed to update payout status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRelease = async (id) => {
    if (!window.confirm('Are you sure you want to release this payout? Information will be sent to Stripe/Bank.')) return;
    
    setActionLoading(id);
    try {
      await api.post(`${ENDPOINTS.PAYOUTS.base}/${id}/release`);
      toast.success('Payout released successfully');
      fetchPayouts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to release payout');
    } finally {
      setActionLoading(null);
    }
  };

  const openReasonModal = (payout, action) => {
    setSelectedPayout(payout);
    setModalAction(action);
    setReason('');
    setShowReasonModal(true);
  };

  const handleModalSubmit = async () => {
    if (!reason.trim()) {
      toast.warning('Please provide a reason');
      return;
    }

    setActionLoading(selectedPayout.id);
    try {
      if (modalAction === 'reject') {
        await api.post(`${ENDPOINTS.PAYOUTS.base}/${selectedPayout.id}/reject`, { reason });
        toast.success('Payout rejected and balance refunded');
      } else {
        await api.post(`${ENDPOINTS.PAYOUTS.base}/${selectedPayout.id}/hold`, { reason });
        toast.success('Payout placed on hold');
      }
      setShowReasonModal(false);
      fetchPayouts();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${modalAction} payout`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRowClick = (payout) => {
    setSelectedPayout(payout);
    setProofFile(null);
    setProofPreview(null);
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setShowDetailModal(true);
  };

  const handleProofChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProofFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProofPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleApproveWithProof = async () => {
    if (!proofFile) {
      toast.warning('Please upload a payment proof first');
      return;
    }

    setActionLoading(selectedPayout.id);
    const formData = new FormData();
    formData.append('proof', proofFile);
    formData.append('paymentDate', paymentDate);

    try {
      await api.post(`${ENDPOINTS.PAYOUTS.base}/${selectedPayout.id}/approve`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Payout approved and completed successfully');
      setShowDetailModal(false);
      setProofFile(null);
      setProofPreview(null);
      fetchPayouts();
    } catch (error) {
      console.error('Approval failed:', error);
      toast.error(error.response?.data?.message || 'Failed to approve payout');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (payoutStatus = '') => {
    const status = (payoutStatus || '').toUpperCase();
    switch (status) {
      case 'COMPLETED':
        return <span className="badge badge-success"><FaCheckCircle /> Completed</span>;
      case 'PENDING':
        return <span className="badge badge-warning"><FaClock /> Requested</span>;
      case 'PROCESSING':
        return <span className="badge badge-info"><FaHistory /> Processing</span>;
      case 'ON_HOLD':
        return <span className="badge badge-secondary"><FaPause /> On Hold</span>;
      case 'FAILED':
      case 'REJECTED':
        return <span className="badge badge-danger"><FaTimesCircle /> Rejected</span>;
      default:
        return <span className="badge badge-light">{payoutStatus || 'UNKNOWN'}</span>;
    }
  };

  if (loading && isFirstLoad) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Fetching payouts...</p>
      </div>
    );
  }

  return (
    <div className="payouts-page">
      <div className="page-header">
        <div>
          <h1>Provider Payouts</h1>
          <p className="page-subtitle">Manage provider earnings and payment releases</p>
        </div>
      </div>

      {/* Stats Section */}
      {/* <div className="payouts-stats">
        <div className="stat-mini-card">
          <div className="stat-mini-icon">
            <FaMoneyCheckAlt />
          </div>
          <div className="stat-mini-content">
            <h3>${Number(stats.totalAmount).toLocaleString()}</h3>
            <p>Total Paid Out</p>
          </div>
        </div>
        <div className="stat-mini-card">
          <div className="stat-mini-icon warning">
            <FaHandHoldingUsd />
          </div>
          <div className="stat-mini-content">
            <h3>${Number(stats.pendingAmount).toLocaleString()}</h3>
            <p>Pending Requests</p>
          </div>
        </div>
        <div className="stat-mini-card">
          <div className="stat-mini-icon success">
            <FaCheckCircle />
          </div>
          <div className="stat-mini-content">
            <h3>{stats.completedCount}</h3>
            <p>Successful Payouts</p>
          </div>
        </div>
      </div> */}

      {/* Filters Toolbar */}
      <div className="content-card filters-card">
        <div className="filters-row">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search provider or business..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="search-input"
                style={{ width: '100%', paddingRight: '30px' }}
              />
              {searchTerm && (
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  style={{ position: 'absolute', right: '8px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0', fontSize: '16px', display: 'flex', alignItems: 'center' }}
                  title="Clear search"
                >
                  &#x2715;
                </button>
              )}
            </div>
          </div>

          <div className="filter-group">
            <FaFilter className="filter-icon" />
            <select 
              value={statusFilter} 
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="filter-select"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Requested</option>
              <option value="processing">Processing</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="failed">Rejected/Failed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payouts Table */}
      <div className="content-card" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        <div className="card-header">
          <h3>Payout Records ({pagination.total})</h3>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle custom-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Amount</th>
                <th>Status</th>
                {/* <th>Booking Date</th> */}
                <th>Requested Date</th>
                <th>Actual Paid Date</th>
                <th>Reference</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length > 0 ? (
                payouts.map((payout) => {
                  const normalizedStatus = (payout.status || '').toUpperCase();
                  const isActionable = normalizedStatus === 'PENDING' || normalizedStatus === 'ON_HOLD';
                  
                  return (
                    <tr 
                      key={payout.id} 
                      className="payout-row"
                      onClick={() => handleRowClick(payout)}
                    >
                      <td>
                        <div className="provider-cell">
                          <div className="user-avatar provider">
                            {payout.provider?.firstName?.charAt(0) || 'P'}
                          </div>
                          <div className="user-info-min">
                            <div className="user-name">
                              {payout.provider?.firstName} {payout.provider?.lastName}
                            </div>
                            {payout.provider?.businessName && (
                              <div className="user-subtext">{payout.provider.businessName}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="amount-payout-wrapper">
                          <span className="amount-payout-net" title="Net Amount">${Number(payout.netAmount || payout.amount).toLocaleString()}</span>
                          {(payout.gatewayFee > 0) && (
                            <div className="amount-payout-breakdown">
                              <small>Gross: ${Number(payout.amount).toLocaleString()}</small>
                              <small>Fee: -${Number(payout.gatewayFee).toLocaleString()}</small>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{getStatusBadge(payout.status)}</td>
                      {/* <td>{payout.periodStart ? new Date(payout.periodStart).toLocaleDateString() : '-'}</td> */}
                      <td>{new Date(payout.createdAt).toLocaleDateString()}</td>
                      <td>{(payout.manualPaymentDate || payout.completedAt) ? new Date(payout.manualPaymentDate || payout.completedAt).toLocaleDateString() : '-'}</td>
                      <td>
                        {payout.stripeTransferId ? (
                          <code className="transaction-id">{payout.stripeTransferId}</code>
                        ) : (
                          <span className="payout-number">{payout.payoutNumber}</span>
                        )}
                      </td>
                      <td className="text-end">
                        <div className="action-buttons">
                          {(normalizedStatus === 'PENDING' || normalizedStatus === 'PROCESSING' || normalizedStatus === 'ON_HOLD') && (
                            <>
                              {normalizedStatus === 'PENDING' && (
                                <button 
                                  className="btn-action process" 
                                  title="Mark as Processing"
                                  disabled={actionLoading === payout.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleProcess(payout.id);
                                  }}
                                >
                                  <FaHistory />
                                </button>
                              )}
                              {(normalizedStatus === 'PROCESSING' || normalizedStatus === 'ON_HOLD' || normalizedStatus === 'PENDING') && (
                                <button 
                                  className="btn-action approve" 
                                  title="Approve with Proof"
                                  disabled={actionLoading === payout.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRowClick(payout);
                                  }}
                                >
                                  <FaCheckCircle />
                                </button>
                              )}
                              <button 
                                className="btn-action hold" 
                                title="Place on Hold"
                                disabled={actionLoading === payout.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openReasonModal(payout, 'hold');
                                }}
                              >
                                <FaPause />
                              </button>
                              <button 
                                className="btn-action reject" 
                                title="Reject & Refund"
                                disabled={actionLoading === payout.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openReasonModal(payout, 'reject');
                                }}
                              >
                                <FaTimesCircle />
                              </button>
                            </>
                          )}
                          {payout.proofUrl && (
                            <a 
                              href={payout.proofUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn-action view"
                              title="View Proof"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FaFileContract />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-5">
                    <p className="text-muted">No payout records found matching your criteria</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <Pagination 
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        />
      </div>

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="payout-modal-overlay">
          <div className="custom-modal">
            <div className="modal-header">
              <h3>{modalAction === 'reject' ? 'Reject Payout' : 'Hold Payout'}</h3>
              <button className="close-btn" onClick={() => setShowReasonModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>
                {modalAction === 'reject' 
                  ? 'Please provide a reason for rejection. This amount will be refunded to the provider\'s balance.' 
                  : 'Provide a reason for placing this payout on hold.'}
              </p>
              <textarea 
                className="modal-textarea"
                placeholder="Enter reason here..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowReasonModal(false)}>Cancel</button>
              <button 
                className={`btn-${modalAction === 'reject' ? 'reject' : 'approve'}`}
                onClick={handleModalSubmit}
                disabled={actionLoading === selectedPayout?.id}
              >
                {actionLoading === selectedPayout?.id ? 'Processing...' : `Confirm ${modalAction === 'reject' ? 'Rejection' : 'Hold'}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Payout Detail Modal */}
      {showDetailModal && selectedPayout && (
        <div className="payout-modal-overlay" onClick={() => {
          setShowDetailModal(false);
          setProofFile(null);
          setProofPreview(null);
        }}>
          <div className="payout-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="payout-modal-header">
              <div>
                <h3>Payout Details</h3>
                <small>{selectedPayout.payoutNumber}</small>
              </div>
              <button className="modal-close-icon" onClick={() => {
                setShowDetailModal(false);
                setProofFile(null);
                setProofPreview(null);
              }}>
                <FaTimes />
              </button>
            </div>

            <div className="payout-modal-body">
              {/* Provider Bank Details */}
              <div className="bank-details-card">
                <div className="section-label">
                  <FaUniversity /> Provider Bank Details
                </div>
                <div className="bank-grid">
                  <div className="bank-item">
                    <label>Account Holder</label>
                    <span>{selectedPayout.provider?.bankAccountHolderName || 'N/A'}</span>
                  </div>
                  {(() => {
                    const isBank1 = SINGAPORE_BANKS.includes(selectedPayout.provider?.bankRoutingNumber);
                    const isBank2 = SINGAPORE_BANKS.includes(selectedPayout.provider?.bankName);

                    let bankName = selectedPayout.provider?.bankName || 'N/A';
                    let routingCode = selectedPayout.provider?.bankRoutingNumber || 'N/A';

                    if (isBank1) {
                      bankName = selectedPayout.provider?.bankRoutingNumber;
                      routingCode = selectedPayout.provider?.bankName || 'N/A';
                    }

                    return (
                      <>
                        <div className="bank-item">
                          <label>Bank Name</label>
                          <span>{bankName}</span>
                        </div>
                        {/* <div className="bank-item">
                          <label>Routing / Branch Code</label>
                          <span>{routingCode}</span>
                        </div> */}
                      </>
                    );
                  })()}
                  <div className="bank-item account-num">
                    <label>Account Number</label>
                    <span>{selectedPayout.provider?.bankAccountNumber || 'N/A'}</span>
                  </div>
                  <div className="bank-item">
                    <label>Amount to Transfer</label>
                    <span style={{ color: '#10b981', fontSize: '1.2rem' }}>
                      ${Number(selectedPayout.netAmount || selectedPayout.amount).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Manual Payment Date */}
              <div className="payment-date-card">
                <div className="section-label">
                  <FaClock /> Payment Date
                </div>
                <div className="date-input-wrapper">
                  <label>Have you made this payment?</label>
                  <input 
                    type="date" 
                    className="modal-date-input"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              {/* Upload Proof */}
              <div className="upload-proof-section">
                <div className="section-label">
                  <FaCloudUploadAlt /> Upload Payment Proof
                </div>
                
                {!proofPreview ? (
                  <label className="upload-dropzone">
                    <input 
                      type="file" 
                      hidden 
                      accept="image/*" 
                      onChange={handleProofChange}
                    />
                    <FaCloudUploadAlt className="upload-icon" />
                    <div className="upload-text">
                      <span className="upload-main-text">Click to upload payment receipt</span>
                      <span className="upload-sub-text">PNG, JPG or PDF up to 5MB</span>
                    </div>
                  </label>
                ) : (
                  <div className="proof-preview">
                    <img src={proofPreview} alt="Payment Proof" />
                    <button className="remove-proof" onClick={() => { setProofFile(null); setProofPreview(null); }}>
                      <FaTimes />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="payout-modal-footer">
              <button className="btn-secondary" onClick={() => {
                setShowDetailModal(false);
                setProofFile(null);
                setProofPreview(null);
              }}>
                Cancel
              </button>
              {(selectedPayout.status?.toUpperCase() === 'PENDING' || selectedPayout.status?.toUpperCase() === 'PROCESSING' || selectedPayout.status?.toUpperCase() === 'ON_HOLD') && (
                <button 
                  className="btn-approve-payout"
                  disabled={!proofFile || actionLoading === selectedPayout.id}
                  onClick={handleApproveWithProof}
                >
                  {actionLoading === selectedPayout.id ? 'Processing...' : 'Confirm & Approve'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutsList;